import { Box, Chip, Tab, Tabs } from '@mui/material';
import { Container } from 'Components/Container';
import { Column } from 'Components/Grid';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnEdit, LinkEdit, RowExhibition } from './styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { getSystemById } from 'services/systems-service';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { TabPanel } from 'Components/TabPanel';

export const SystemsShow = () => {
  const { t } = useTranslation(['mapping', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const [tabPosition, setTabPosition] = useState(0);

  const {
    isLoading,
    error,
    data: system,
  } = useQuery('getSystemById', async () => {
    const dataSystem = await getSystemById(id);

    return dataSystem.status === 200 ? dataSystem.data : null;
  });
  const onClickEdit = () => {
    navigate('/systems/edit/' + id);
  };

  return (
    <>
      {!isLoading && !error && (
        <Container title={`${system?.name}`} breadcrumb={t('mapping:systems.show.breadcrumb', { name: system?.name ?? '' })} linkPage="/systems">
          <Box sx={{ width: '100%' }} className="container">
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabPosition}
                onChange={(data, newValue) => setTabPosition(newValue)}
                aria-label="basic tabs example"
              >
                <Tab
                  label={t('mapping:systems.show.tabs.details')}
                  id="tab-details"
                  aria-controls="tabpanel-details"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
                <Tab
                  label={t('mapping:systems.show.tabs.links')}
                  id="tab-links"
                  aria-controls="tabpanel-links"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
              </Tabs>
            </Box>
            <TabPanel value={tabPosition} index={0} id="tabpanel-details" ariaLabel="tab-details">
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.name')}</Column>
                <Column>{system.name}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.criticality')}</Column>
                <Column>
                  {system.criticity ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div 
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: (() => {
                            const colors = {
                              '1': '#22c55e', // Verde
                              '2': '#84cc16', // Verde claro
                              '3': '#eab308', // Amarelo
                              '4': '#f97316', // Laranja
                              '5': '#ef4444'  // Vermelho
                            };
                            return colors[system.criticity] || '#6b7280';
                          })(),
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      />
                      <span>
                        {t(`mapping:systems.criticality.${system.criticity}`, { defaultValue: system.criticity })}
                      </span>
                    </div>
                  ) : '-'}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.responsible')}</Column>
                <Column>{system.user}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.saas')}</Column>
                <Column>{system.saas ? t('common:boolean.yes') : t('common:boolean.no')}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.developedInHouse')}</Column>
                <Column>{system.developedInHouse ? t('common:boolean.yes') : t('common:boolean.no')}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.tags')}</Column>
                <Column>
                  {system.tags?.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      variant="outlined"
                      color="primary"
                      sx={{
                        width: 'fit-content',
                      }}
                    />
                  ))}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.description')}</Column>
                <Column>{system.description}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.database')}</Column>
                <Column>{system.dataBase}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
            </TabPanel>
            <TabPanel value={tabPosition} index={1} id="tabpanel-links" ariaLabel="tab-links">
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.areas')}</Column>
                <Column>
                  {system?.areas?.map(area => (
                    <Chip
                      key={area.name}
                      label={area.name}
                      variant="outlined"
                      color="primary"
                      sx={{
                        width: 'fit-content',
                        display: area.archived ? 'none' : '',
                      }}
                    />
                  ))}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.processes')}</Column>
                <Column>
                  {system?.processes?.map(process => (
                    <Chip
                      key={process.name}
                      label={process.name}
                      variant="outlined"
                      color="primary"
                      sx={{
                        width: 'fit-content',
                        display: process.archived ? 'none' : '',
                      }}
                    />
                  ))}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('mapping:systems.show.fields.assets')}</Column>
                <Column>
                  {system?.assets?.map(asset => (
                    <Chip
                      key={asset.name}
                      label={asset.name}
                      variant="outlined"
                      color="primary"
                      sx={{
                        width: 'fit-content',
                        display: asset.archived ? 'none' : '',
                      }}
                    />
                  ))}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('mapping:systems.show.editLink')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
            </TabPanel>
          </Box>
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>{t('mapping:systems.show.error')}</div>}
    </>
  );
};
